import Guard from 'arg-guard'

import Edits from './edits'

import Logger from '@/logger'
import ServiceFactory from '@/services/service-factory'
import StatusPoller from '@/helpers/status-poller'

import Store from '@/store'
import { ACTIONS } from '@/store/actions'
import { MUTATIONS } from '@/store/mutations'

import AuthManager from '@/auth/auth-manager'
import PlanBuilderManager from '@/plan-builder-manager'
import SchemeServicingManager from '@/scheme-servicing-manager'

import AppError from '@/models/app-error'
import EditablePropertyDataItem from '@/models/editable-property-data-item'
import Home from '@/models/home'
import Property from '@/models/property'
import PropertyInfo from '@/models/property-info'
import ServiceOperation from '@/services/service-operation'

const loggingCategorySearch = 'Address'
const loggingCategoryPrimary = 'MyHome'

/**
 * Callback function to execute when the loadDataCallback is complete.
 * @callback PostLoadDataCallback
 * @param {Property?} updatedProperty
 * @returns {Promise<LinkedSchemeData>}
 */

/**
 * Function to load data if available or after polling completes.
 * @param {PostLoadDataCallback} postLoadDataCallback
 * @returns {Promise<LinkedSchemeData>}
 */
const loadDataCallbackAsync = async(postLoadDataCallback) => {
    try {
        Store.commit(MUTATIONS.IMPROVEMENTS.clearSavings)
        await PlanBuilderManager.Savings.loadInitialSavingsIntoStore()
        Store.commit(MUTATIONS.setPropertyCalculationComplete)
        Store.commit(MUTATIONS.MYHOME.clearErrors)
        Store.commit(MUTATIONS.MYHOME.clearWarnings)
        const locallyUpdatedProperty = Store.state.property
        const retVal = await postLoadDataCallback(locallyUpdatedProperty)
        Logger.logObject(locallyUpdatedProperty, 'Property calculated', loggingCategorySearch)
        return retVal
    } catch (err) {
        throw err
    }
}

/**
 * Handles processing the ensureResult parameter.
 * @param {EnsureOperationResult} ensureResult The result to process.
 * @param {Property} property The property that's been selected.
 * @param {PostLoadDataCallback} postLoadDataCallback The callback to execute after calc is completed and successful.
 * @param {boolean} refresh Set to 'true' if called by a refresh action.
 * @returns {Promise<boolean>} A flag to indicate if the result succeeded or failed.
 */
const handleEnsureResultAsync = async(ensureResult, property, postLoadDataCallback, refresh = false) => {
    const processText = refresh ? 'refreshed' : 'chosen'

    // Skip out if no data comes back.
    if (!ensureResult)
        return true

    if (!Store.state.propertyCalculationInProgress) {
        Store.commit(MUTATIONS.setPropertyCalculationInProgress)

        if (ensureResult.success) {
            if (!ensureResult.calcNeeded) {
                Logger.log(`Property ${ processText } - Calculations already done.`, loggingCategorySearch)
                await loadDataCallbackAsync(postLoadDataCallback)
            } else {
                Logger.log(`Property ${ processText } - Polling for calculation results.`, loggingCategorySearch)
                const pollingText = refresh
                    ? `calculations for ${ property.address } with ID ${ property.id } in ${ property.postcode } after refresh`
                    : `calculations when choosing ${ property.address } with ID ${ property.id } in ${ property.postcode }`
                await StatusPoller.executeAfterPollAsync(pollingText, 'checkAnalysisCalculationAsync',
                    () => loadDataCallbackAsync(postLoadDataCallback), property.id)
            }
        } else {
            Store.commit(MUTATIONS.setPropertyCalculationComplete)
            Logger.error('Property data has errors - Load aborted: Fix property data to continue.', loggingCategoryPrimary)
            Logger.logObject(ensureResult, `Property ${ processText } - Data has errors - Load aborted.`, loggingCategoryPrimary)
            Store.commit(MUTATIONS.MYHOME.setErrors, ensureResult.errors)
            Store.commit(MUTATIONS.MYHOME.setWarnings, ensureResult.warnings)
        }
    }

    return refresh || ensureResult.success
}

/*
 * The Property Manager deals with interactions with the services and data around managing and querying properties.
 */
const PropertyManager = {

    Edits,

    /**
     * Chooses the property for the app to operate on.
     * @param {Property} property
     * @returns {Promise<Object>} The response.
     * @throws {TypeError} The property is null or undefined.
     */
    async choosePropertyAsync(property) {
        Guard.throwIf(property, 'property')

        /**
         * @type {PostLoadDataCallback}
         */
        const postLoadCallback = async(updatedProperty) => {
            return await SchemeServicingManager.loadLinkedSchemeDataAsync(updatedProperty)
        }

        try {
            Logger.logObject(property, 'Selecting property', loggingCategorySearch)
            const result = await SchemeServicingManager.loadLinkedSchemeDataAsync(property)
            await AuthManager.welcomeGuestIfNoLoggedInUserAsync(property)

            const ensureResult = await PlanBuilderManager.ApiData.ensureInitiativesAsync(property.id)
            await handleEnsureResultAsync(ensureResult, property, postLoadCallback)

            return result
        } catch (err) {
            Logger.warn(`Unable to choose a property: ${ err.message ? err.message : err }`, loggingCategoryPrimary)
            throw err
        }
    },

    /**
     * Clears the property in state.
     * @returns {Promise<Object>} The response.
     */
    async clearPropertyAsync() {
        try {
            Logger.log('Clearing property data', loggingCategoryPrimary)
            await Store.dispatch(ACTIONS.updateChosenProperty, null)
            return await Store.dispatch(ACTIONS.IMPROVEMENTS.clearProjectData)
        } catch (err) {
            Logger.warn(`Unable to clear the property: ${ err.message ? err.message : err }`, loggingCategorySearch)
            throw err
        }
    },

    /**
     * Ensures that the base metrics data is loaded into state.
     * The data will not reload if the state is already loaded.
     * @param {number} addressId The address identifier.
     * @returns {Promise<ServiceOperation>} The service result.
     * @throws {TypeError} The address identifier is null or undefined.
     */
    async ensureBaseStatisticsAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')

        if (Store.state.metricsLoaded) {
            Logger.log('Base metrics already loaded', loggingCategoryPrimary)
            return ServiceOperation.immediate
        }

        return await PropertyManager.loadHomeDataAsync(addressId, 'ensure base metrics', false)
    },

    /**
     * Determines if the provided postcode is valid and holds relevant residential properties.
     * @param {string} postcode
     * @returns {boolean} A flag to indicate if the postcode is relevant or not.
     * @returns {Promise<Object>} The output data.
     */
    async isPostcodeRelevantAsync(postcode) {
        try {
            const addressData = await this.loadAddressesForAsync(postcode)

            return addressData && addressData.length > 0
        } catch (err) {
            Logger.warn(`Unable to check postcode: ${ err.message ? err.message : err }`, loggingCategorySearch)
            return false
        }
    },

    /**
     * Loads all addresses associated with the provided postcode.
     * @param {string} postcode
     * @returns {Array} A load of addresses to display.
     * @returns {Promise<Object>} The output data.
     */
    async loadAddressesForAsync(postcode) {
        if (postcode in Store.state.searchedAddresses && Store.state.searchedAddresses[postcode])
            return Store.state.searchedAddresses[postcode]

        try {
            const service = ServiceFactory.address()
            const operationState = await service.searchAsync(postcode)

            if (!operationState.succeeded) operationState.throw()

            Store.commit(MUTATIONS.clearSearchedAddresses)
            Store.commit(MUTATIONS.setSearchedAddresses, { postcode, addressList: operationState.data })

            return operationState.data
        } catch (err) {
            Logger.warn(`Unable to find addresses: ${ err.message ? err.message : err }`, loggingCategorySearch)
            throw err
        }
    },

    /**
     * Ensures that the base metrics data is loaded into state.
     * The data will not reload if the state is already loaded.
     * @param {number} addressId The address identifier.
     * @param {boolean} [force=false]
     * @returns {Promise<ServiceOperation>} The service result.
     * @throws {TypeError} The address identifier is null or undefined.
     */
    async loadBaseStatisticsAsync(addressId, force = false) {
        Guard.throwIf(addressId, 'addressId')

        if (!force && Store.state.metricsLoaded) {
            Logger.log('Base metrics already loaded', loggingCategoryPrimary)
            return ServiceOperation.immediate
        }

        try {
            const service = ServiceFactory.saving()
            const operationState = await service.loadBaseStatisticsAsync(addressId)

            if (operationState.error) {
                // noinspection ExceptionCaughtLocallyJS
                throw new Error(operationState.error)
            } else {
                Logger.logObject(operationState, 'Successful load of base statistics', loggingCategoryPrimary)
            }

            if (operationState.data) {
                Store.commit(MUTATIONS.METRICS.setBase, operationState.data)
                return operationState
            }
        } catch (err) {
            Logger.log('Failed to load base statistics', loggingCategoryPrimary)
            if (addressId !== 0) {
                Store.commit(MUTATIONS.logAppError, new AppError('Unable to load base statistics for this property', true))
                throw err
            }
        }
    },

    /**
     * Loads home data into state.
     * @param {number} addressId The address identifier.
     * @param {string} [msgFragment='load home data'] Fragment of the message to log.
     * @param {boolean} [setHomeState=true] Set false to prevent main home data being updated in state.
     * @returns {Promise<ServiceOperation>} The service result.
     * @throws {TypeError} The address identifier is null or undefined.
     */
    async loadHomeDataAsync(addressId, msgFragment = 'load home data', setHomeState = true) {
        Guard.throwIf(addressId, 'addressId')

        try {
            const service = ServiceFactory.address()
            const operationState = await service.loadMyHomeDataAsync(addressId)

            if (operationState.error) {
                // noinspection ExceptionCaughtLocallyJS
                throw new Error(operationState.error)
            }

            if (setHomeState)
                Store.commit(MUTATIONS.MYHOME.setMyHome, operationState.data)

            if (operationState.data.baseResult) {
                if (!Store.state.metricsLoaded)
                    Store.commit(MUTATIONS.METRICS.setBase, operationState.data.baseResult)
            } else {
                try {
                    await PropertyManager.pollBaseStatistics(addressId)
                } catch (err) {
                    Logger.logObject(err, `Partial fail state on ${ msgFragment } while polling`, loggingCategoryPrimary)
                }
            }

            return operationState
        } catch (err) {
            Logger.log(`Failed to ${ msgFragment }`, loggingCategoryPrimary)
            if (addressId !== 0) {
                Store.commit(MUTATIONS.logAppError, new AppError('Unable to load data about this property', true))
                throw err
            }
        }
    },

    /**
     * Loads home data for the specified address ID into state, clearing any improvement / project state data as well.
     * @param {number} addressId The address identifier.
     * @param {boolean} [force=false]
     * @returns {Promise<?Home>} home data model.
     */
    async loadHomeDataIfNeededOrForcedAsync(addressId, force = false) {
        try {
            if (!Store.state.myHome.loaded || force) {
                Store.commit(MUTATIONS.MYHOME.resetState)
                Store.commit(MUTATIONS.IMPROVEMENTS.resetState)

                const operationState = await PropertyManager.loadHomeDataAsync(addressId)

                if (!operationState.succeeded) operationState.throw()
                return Home.ensure(operationState.data)
            }
        } catch {
            Logger.logObject(addressId, 'Unserviced property', loggingCategoryPrimary)
        }

        return null
    },

    /**
     * Polls to check the state of base statistics, waiting to reload them.
     * @param {number} addressId The address identifier.
     * @returns {Promise<ServiceOperation>} The service result.
     * @throws {TypeError} The address identifier is null or undefined.
     */
    pollBaseStatistics(addressId) {
        Guard.throwIf(addressId, 'addressId')

        try {
            return StatusPoller.executeAfterPollAsync('Poll base statistics', 'checkBaseStatisticsAsync', async() => {
                await PropertyManager.loadBaseStatisticsAsync(addressId, true)
            }, addressId)
        } catch (err) {
            Logger.log('Failed to poll for base statistics', loggingCategoryPrimary)
            if (addressId !== 0) {
                Store.commit(MUTATIONS.logAppError, new AppError(err, true))
                throw err
            }
        }
    },

    /**
     * Refreshes the data associated to the property for the app to operate on.
     * @returns {boolean} The completion flag.
     * @returns {Promise<boolean>} A flag to indicate success.
     */
    async refreshPropertyAsync() {

        /**
         * @type {PostLoadDataCallback}
         */
        const postLoadCallback = async(updatedProperty) => {
            return await Store.dispatch(ACTIONS.updateChosenProperty, new PropertyInfo(updatedProperty))
        }

        try {
            const propertyData = Store.state.property
            const property = Property.ensure(propertyData)
            Logger.logObject(property, 'Refreshing property', loggingCategorySearch)

            const ensureResult = await PlanBuilderManager.ApiData.ensureInitiativesAsync(property.id)
            return await handleEnsureResultAsync(ensureResult, property, postLoadCallback, true)
        } catch (err) {
            Logger.warn(`Unable to choose a property: ${ err.message ? err.message : err }`, loggingCategorySearch)
            if (err.message !== 'No address ID specified (0)')
                throw err
        }
    },

    /**
     * Stores properties linked to the logged in user in storage.
     * @param {Array<Property>} properties An array of properties to store. This will replace any existing entries.
     * @returns {Array<Property>} The array of properties passed in are returned back out.
     */
    storeLinkedProperties(properties) {
        Guard.throwIf(properties, 'properties')

        Store.commit(MUTATIONS.setUserLinkedProperties, properties)
        Logger.logObject(properties, 'User linked properties stored', loggingCategoryPrimary)
    },

    /**
     * Switches the chosen property to one of the available user linked properties.
     * @param {number} addressId The address identifier.
     * @returns {Promise<Property>} The newly active property switched to.
     * @throws {TypeError} The address identifier is null or undefined.
     * @throws {Error} The address identifier is not present in linked properties in storage or is not the current stored property.
     */
    async switchChosenPropertyAsync(addressId) {
        Guard.throwIf(addressId, 'addressId')

        if (Store.state.property && Store.state.property.id === addressId && Store.state.property.address !== '') {
            Logger.logObject(Store.state.property, `Current property matches address ID ${ addressId }`, loggingCategoryPrimary)
            return Store.state.property
        }

        Logger.logObject(Store.state.userLinkedProperties, `Looking for ${ addressId } in userLinkedProperties`, loggingCategoryPrimary)

        const newProperty = Store.state.userLinkedProperties.find(p => p.id === addressId)
        if (!newProperty)
            throw new Error(`The addressId ${ addressId } is not present in user linked properties in storage.`)

        try {
            await SchemeServicingManager.loadLinkedSchemeDataAsync(newProperty)

            Store.commit(MUTATIONS.selectProperty, newProperty)
            Store.commit(MUTATIONS.IMPROVEMENTS.resetState)

            return newProperty
        } catch (err) {
            throw err
        }
    },

    /**
     * Updates any edited segment fields in state and to the API.
     * @param {number} addressId The address identifier.
     * @param {string} segmentKey The key of the segment to update
     * @returns {Promise<ServiceOperation>} The service result.
     * @throws {TypeError} The address identifier is null or undefined.
     * @throws {TypeError} The segment key is null or undefined.
     */
    async updateSegmentFieldsAsync(addressId, segmentKey) {
        Guard.throwIf(addressId, 'addressId')
        Guard.throwIf(segmentKey, 'segmentKey')

        const changedItems = Store.state.myHome.edits.map(edit => EditablePropertyDataItem.ensure(edit))
            .filter(edit => edit.payload != null)
            .map(edit => edit.payload)

        if (changedItems.length) {
            try {
                const service = ServiceFactory.address()
                const operationState = await service.updateSegmentAsync(addressId, segmentKey, changedItems)

                if (operationState.error) {
                    // noinspection ExceptionCaughtLocallyJS
                    throw new Error(operationState.error)
                }

                await PropertyManager.loadHomeDataIfNeededOrForcedAsync(addressId, true)
                return operationState
            } catch (err) {
                Logger.log('Failed to update segment data', loggingCategoryPrimary)
                Store.commit(MUTATIONS.logAppError, new AppError(err, true))
                throw err
            }
        } else {
            Logger.log('No segment data to update', loggingCategoryPrimary)
        }
    },
}

export default PropertyManager
